import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../auth';
import { useSubscription } from './useSubscription';
import {
  SECTION_DISCOVERY_HINTS,
  type SectionKey,
  type HintCopy,
} from '../config/sectionDiscoveryHints';
import { trackOnboardingEvent } from '../analytics/onboardingEvents';

export interface SectionHintState {
  dismissed_variant?: 'discovery' | 'advanced' | null;
  dismissed_at?: string;
  last_shown_at?: string;
}

type SectionHintsState = Record<string, SectionHintState>;

export interface UseSectionHintResult {
  variant: 'discovery' | 'advanced' | null;
  copy: HintCopy | null;
  onDismiss: () => Promise<void>;
  onCtaClick: (which: 'primary' | 'secondary') => void;
}

const TRIAL_PLUS_30D_MS = 30 * 86400_000;

export function useSectionHint(section: SectionKey, count: number): UseSectionHintResult {
  const { user, userProfile, refreshProfile } = useAuth();
  const subscription = useSubscription();
  const config = useMemo(
    () => SECTION_DISCOVERY_HINTS.find((h) => h.section === section),
    [section],
  );

  // Dismiss ottimistico: userProfile nel context non si aggiorna con la sola
  // UPDATE su Supabase, quindi senza questo la card resterebbe visibile fino al
  // reload. Nasconde subito; refreshProfile() poi allinea il context.
  const [dismissedLocal, setDismissedLocal] = useState<'discovery' | 'advanced' | null>(null);

  const state = useMemo(() => {
    const all = (userProfile as { section_hints_state?: SectionHintsState } | null)?.section_hints_state ?? {};
    return all[section] ?? {};
  }, [userProfile, section]);

  const variant = useMemo<'discovery' | 'advanced' | null>(() => {
    if (!config) return null;
    // Gate 1: onboarding phase (trial + 30gg post-conversion)
    if (!subscription) return null;
    const inTrial = subscription.isTrialActive;
    const recentlyConverted =
      subscription.organization?.subscription_status === 'active' &&
      subscription.organization?.last_payment_at != null &&
      Date.now() - new Date(subscription.organization.last_payment_at).getTime() < TRIAL_PLUS_30D_MS;
    if (!inTrial && !recentlyConverted) return null;

    // Gate 2: variant
    let v: 'discovery' | 'advanced' | null = null;
    if (count === 0) v = 'discovery';
    else if (count > config.discoveryThreshold) v = 'advanced';
    else return null;

    // Gate 3: dismissed (persistito su DB oppure appena chiuso in questa sessione)
    if (state.dismissed_variant === v || dismissedLocal === v) return null;

    // Gate 4: advanced cap 7gg
    if (v === 'advanced' && state.last_shown_at) {
      const last = new Date(state.last_shown_at).getTime();
      if (Date.now() - last < 7 * 86400_000) return null;
    }

    return v;
  }, [config, subscription, count, state, dismissedLocal]);

  const copy = useMemo<HintCopy | null>(() => {
    if (!config || !variant) return null;
    const base = config[variant];
    return { ...base, title: base.title.replace('{count}', String(count)) };
  }, [config, variant, count]);

  // Marca last_shown_at quando il banner appare (side-effect)
  useEffect(() => {
    if (!variant || !user) return;
    const all = (userProfile as { section_hints_state?: SectionHintsState } | null)?.section_hints_state ?? {};
    const next: SectionHintsState = {
      ...all,
      [section]: { ...all[section], last_shown_at: new Date().toISOString() },
    };
    void supabase.from('user_profiles').update({ section_hints_state: next }).eq('id', user.id);
    trackOnboardingEvent('flusia.discovery.section_hint_viewed', { section, variant });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  const onDismiss = useCallback(async () => {
    if (!user || !variant) return;
    // 1) Nascondi subito (ottimistico), così la card sparisce al click.
    setDismissedLocal(variant);
    trackOnboardingEvent('flusia.discovery.section_hint_dismissed', { section, variant });
    const all = (userProfile as { section_hints_state?: SectionHintsState } | null)?.section_hints_state ?? {};
    const next: SectionHintsState = {
      ...all,
      [section]: {
        ...all[section],
        dismissed_variant: variant,
        dismissed_at: new Date().toISOString(),
      },
    };
    // 2) Persisti su DB, poi allinea il context (così resta chiusa anche
    //    navigando via e tornando, senza dover ricaricare).
    const { error } = await supabase.from('user_profiles').update({ section_hints_state: next }).eq('id', user.id);
    if (!error) {
      await refreshProfile();
    }
  }, [user, userProfile, section, variant, refreshProfile]);

  const onCtaClick = useCallback((which: 'primary' | 'secondary') => {
    trackOnboardingEvent('flusia.discovery.section_hint_cta_clicked', { section, variant, cta: which });
  }, [section, variant]);

  return { variant, copy, onDismiss, onCtaClick };
}
